/**
 * useGlobalTheatre — resolves the Global Intelligence container id.
 *
 * Wraps `GET /api/v1/environment/global-theatre` (cycle FE-1 sprint-236,
 * SDD §6.3 option (a)). The backend resolves `GLOBAL_INTELLIGENCE_THEATRE_ID`
 * server-side and returns `{ theatre_id }` or a deterministic 404 when the
 * env var is unset / the row is absent. 404 is an HONEST state, not an error:
 * the hook maps it to `null` so consumers render the honest-empty ambient
 * surface (no retry loop — the 404 will not heal between attempts).
 *
 * Ontology invariant (SDD §6.3.1, operator-locked): the object behind this
 * id is the permanent global Dossier. Internal names may say "theatre";
 * operator-facing strings must say "Global Intelligence" — never "Theatre".
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from '../api/client';

export interface GlobalTheatreResponse {
  theatre_id: string;
}

export function useGlobalTheatre(
  enabled = true,
): UseQueryResult<GlobalTheatreResponse | null> {
  return useQuery<GlobalTheatreResponse | null>({
    queryKey: ['environment', 'global-theatre'],
    queryFn: async () => {
      try {
        const { data } = await apiClient.get<GlobalTheatreResponse>(
          '/api/v1/environment/global-theatre',
        );
        return data?.theatre_id ? data : null;
      } catch (err) {
        // Deterministic 404 = global container unset (honest skip, no fallback).
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}
